import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiSearch, FiFilter, FiGrid, FiList, FiX, FiChevronDown, FiHome, FiHeart, FiMapPin, FiColumns } from 'react-icons/fi';
import { shedService } from '../services/shedService';
import { shedTypes, shedMaterials, shedSizes } from '../data/sampleSheds';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useCompare } from '../context/CompareContext';
import LocationFilter, { haversineDistance, getCoordsForLocation } from '../components/LocationFilter';

const ShedInventory = () => {
  const [sheds, setSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [userCoords, setUserCoords] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [maxDistance, setMaxDistance] = useState(0);
  const [locationPrompted, setLocationPrompted] = useState(false);
  const { addToCart, isInCart, cartCount } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const { addToCompare, isInCompare, canAddMore } = useCompare();

  const handleAddToCart = (e, shed) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(shed);
  };

  const handleShare = async (e, shed) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/shed/${shed.id}`;
    const shareTitle = shed.name || 'Check out this shed';
    const shareText = `${shareTitle} - ${shed.size} ${shed.shedType}`;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Link copied to clipboard!');
      }
    }
  };
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    shedType: '',
    material: '',
    size: '',
    priceMin: '',
    priceMax: '',
    hasLoft: false,
    hasElectrical: false,
    deliveryIncluded: false
  });
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadSheds();
  }, []);

  useEffect(() => {
    if (!locationPrompted) {
      setLocationPrompted(true);
      const saved = sessionStorage.getItem('shedx_user_coords');
      if (saved) {
        try {
          setUserCoords(JSON.parse(saved));
          setLocationStatus('granted');
        } catch (e) { /* ignore */ }
      } else {
        requestLocation();
      }
    }
  }, [locationPrompted]);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      return;
    }
    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserCoords(coords);
        setLocationStatus('granted');
        setSortBy('nearest');
        sessionStorage.setItem('shedx_user_coords', JSON.stringify(coords));
      },
      () => {
        setLocationStatus('denied');
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  const loadSheds = async () => {
    setLoading(true);
    try {
      const result = await shedService.getAvailableSheds();
      if (!result.error && result.data) {
        setSheds(result.data);
      }
    } catch (err) {
      console.error('Error loading sheds:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSheds = useMemo(() => {
    let result = [...sheds];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(shed =>
        shed.name?.toLowerCase().includes(term) ||
        shed.brand?.toLowerCase().includes(term) ||
        shed.model?.toLowerCase().includes(term) ||
        shed.shedType?.toLowerCase().includes(term) ||
        shed.description?.toLowerCase().includes(term)
      );
    }

    // Type filter
    if (filters.shedType) {
      result = result.filter(shed => 
        shed.shedType?.toLowerCase() === filters.shedType.toLowerCase()
      );
    }

    // Material filter
    if (filters.material) {
      result = result.filter(shed => 
        shed.material?.toLowerCase() === filters.material.toLowerCase()
      );
    }

    // Size filter (partial match so typing '12x' shows 12x14, 12x16, etc.)
    if (filters.size) {
      const sizeFilter = filters.size.toLowerCase().trim();
      result = result.filter(shed => shed.size?.toLowerCase().includes(sizeFilter));
    }

    // Price filters
    if (filters.priceMin) {
      result = result.filter(shed => (shed.salePrice || shed.price) >= parseInt(filters.priceMin));
    }
    if (filters.priceMax) {
      result = result.filter(shed => (shed.salePrice || shed.price) <= parseInt(filters.priceMax));
    }

    // Feature filters
    if (filters.hasLoft) {
      result = result.filter(shed => shed.loft);
    }
    if (filters.hasElectrical) {
      result = result.filter(shed => shed.electrical);
    }
    if (filters.deliveryIncluded) {
      result = result.filter(shed => shed.deliveryIncluded);
    }

    // Location filter
    if (selectedLocation) {
      result = result.filter(shed =>
        shed.dealerLocation?.toLowerCase().trim() === selectedLocation.toLowerCase().trim()
      );
    }

    // Distance filter
    if (userCoords && maxDistance > 0) {
      result = result.filter(shed => {
        const coords = getCoordsForLocation(shed.dealerLocation);
        if (!coords) return true;
        const dist = haversineDistance(userCoords.lat, userCoords.lng, coords.lat, coords.lng);
        return dist <= maxDistance;
      });
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      case 'price-high':
        result.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
        break;
      case 'size-small':
        result.sort((a, b) => (a.squareFeet || 0) - (b.squareFeet || 0));
        break;
      case 'size-large':
        result.sort((a, b) => (b.squareFeet || 0) - (a.squareFeet || 0));
        break;
      case 'nearest':
        if (userCoords) {
          result.sort((a, b) => {
            const ac = getCoordsForLocation(a.dealerLocation);
            const bc = getCoordsForLocation(b.dealerLocation);
            const ad = ac ? haversineDistance(userCoords.lat, userCoords.lng, ac.lat, ac.lng) : 99999;
            const bd = bc ? haversineDistance(userCoords.lat, userCoords.lng, bc.lat, bc.lng) : 99999;
            return ad - bd;
          });
        }
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return result;
  }, [sheds, searchTerm, filters, sortBy, selectedLocation, userCoords, maxDistance]);

  const clearFilters = () => {
    setFilters({
      shedType: '',
      material: '',
      size: '',
      priceMin: '',
      priceMax: '',
      hasLoft: false,
      hasElectrical: false,
      deliveryIncluded: false
    });
    setSearchTerm('');
    setSelectedLocation(null);
    setMaxDistance(0);
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== '').length + (searchTerm ? 1 : 0) + (selectedLocation ? 1 : 0) + (maxDistance > 0 ? 1 : 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getRtoMonthly = (shed) => {
    const base = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
    const monthly = Math.round((base * 1.45) / 36);
    return formatPrice(monthly);
  };

  return (
    <>
      <Helmet>
        <title>Shed Inventory | Quality Storage Sheds, Barns & Workshops</title>
        <meta name="description" content="Browse our selection of quality storage sheds, barns, workshops, and outdoor buildings. Find the perfect shed for your needs with free delivery available." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        {/* Page Header */}
        <div className="border-b border-gray-200 py-8">
          <div className="container mx-auto px-4">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Inventory</h1>
            <p className="text-sm text-gray-400">
              {sheds.length} sheds available &middot; Free delivery on most models
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Search and Filter Bar */}
          <div className="bg-white border border-gray-200 p-4 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Location Filter */}
              <LocationFilter
                sheds={sheds}
                selectedLocation={selectedLocation}
                onLocationChange={setSelectedLocation}
                maxDistance={maxDistance}
                onMaxDistanceChange={setMaxDistance}
                userCoords={userCoords}
                onUserCoordsChange={setUserCoords}
                onRequestLocation={requestLocation}
                locationStatus={locationStatus}
              />

              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search sheds by name, style, or features..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none"
                />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-200 px-4 py-3 pr-10 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none"
                >
                  <option value="newest">Newest First</option>
                  {userCoords && <option value="nearest">Nearest First</option>}
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="size-small">Size: Small to Large</option>
                  <option value="size-large">Size: Large to Small</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-6 py-3 transition-colors ${
                  showFilters ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FiFilter />
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-gray-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* View Toggle */}
              <div className="flex border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <FiGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <FiList className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Filter Panel */}
              {showFilters && (
                <div>
                  <div className="pt-6 mt-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Shed Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Shed Type</label>
                        <select
                          value={filters.shedType}
                          onChange={(e) => setFilters({ ...filters, shedType: e.target.value })}
                          className="w-full border border-gray-200 px-4 py-2.5 focus:ring-1 focus:ring-gray-400 outline-none"
                        >
                          <option value="">All Types</option>
                          {shedTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Material */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Material</label>
                        <select
                          value={filters.material}
                          onChange={(e) => setFilters({ ...filters, material: e.target.value })}
                          className="w-full border border-gray-200 px-4 py-2.5 focus:ring-1 focus:ring-gray-400 outline-none"
                        >
                          <option value="">All Materials</option>
                          {shedMaterials.map(material => (
                            <option key={material.value} value={material.value}>{material.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Size — Typeable */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                        <input
                          type="text"
                          list="inventorySizeOptions"
                          value={filters.size}
                          onChange={(e) => setFilters({ ...filters, size: e.target.value })}
                          placeholder="Type a size (e.g. 10x12)"
                          className="w-full border border-gray-200 px-4 py-2.5 focus:ring-1 focus:ring-gray-400 outline-none"
                        />
                        <datalist id="inventorySizeOptions">
                          {[...new Set(sheds.map(s => s.size).filter(Boolean))].sort().map(size => (
                            <option key={size} value={size} />
                          ))}
                        </datalist>
                      </div>

                      {/* Price Range */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            placeholder="Min"
                            value={filters.priceMin}
                            onChange={(e) => setFilters({ ...filters, priceMin: e.target.value })}
                            className="w-1/2 border border-gray-200 px-3 py-2.5 focus:ring-1 focus:ring-gray-400 outline-none"
                          />
                          <input
                            type="number"
                            placeholder="Max"
                            value={filters.priceMax}
                            onChange={(e) => setFilters({ ...filters, priceMax: e.target.value })}
                            className="w-1/2 border border-gray-200 px-3 py-2.5 focus:ring-1 focus:ring-gray-400 outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Feature Checkboxes */}
                    <div className="flex flex-wrap gap-4 mt-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.hasLoft}
                          onChange={(e) => setFilters({ ...filters, hasLoft: e.target.checked })}
                          className="w-4 h-4 text-gray-900 rounded focus:ring-gray-400"
                        />
                        <span className="text-sm text-gray-700">Has Loft</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.hasElectrical}
                          onChange={(e) => setFilters({ ...filters, hasElectrical: e.target.checked })}
                          className="w-4 h-4 text-gray-900 rounded focus:ring-gray-400"
                        />
                        <span className="text-sm text-gray-700">Pre-Wired Electrical</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.deliveryIncluded}
                          onChange={(e) => setFilters({ ...filters, deliveryIncluded: e.target.checked })}
                          className="w-4 h-4 text-gray-900 rounded focus:ring-gray-400"
                        />
                        <span className="text-sm text-gray-700">Free Delivery</span>
                      </label>
                    </div>

                    {activeFilterCount > 0 && (
                      <button
                        onClick={clearFilters}
                        className="mt-4 text-gray-500 hover:text-gray-900 flex items-center gap-1 text-sm"
                      >
                        <FiX className="w-4 h-4" />
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </div>
              )}
          </div>

          {/* Results Count */}
          <div className="mb-6 text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredSheds.length}</span> of {sheds.length} sheds
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                  <div className="h-56 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-8 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredSheds.length === 0 ? (
            <div className="text-center py-16">
              <FiHome className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Sheds Found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your filters or search term</p>
              <button
                onClick={clearFilters}
                className="bg-gray-900 text-white px-6 py-3 hover:bg-gray-800 transition-colors text-sm"
              >
                Clear Filters
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
              {filteredSheds.map((shed) => (
                <div key={shed.id} className="bg-white relative group">
                  {/* Actions */}
                  <div className="absolute top-3 right-3 z-10 flex gap-1">
                    {(canAddMore || isInCompare(shed.id)) && (
                      <button
                        onClick={(e) => { e.preventDefault(); addToCompare(shed); }}
                        className={`p-1.5 transition-colors ${isInCompare(shed.id) ? 'bg-gray-900 text-white' : 'bg-white/80 hover:bg-white text-gray-600'}`}
                        title={isInCompare(shed.id) ? 'Added to compare' : 'Compare'}
                      >
                        <FiColumns className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.preventDefault(); toggleWishlist(shed.id); }}
                      className="p-1.5 bg-white/80 hover:bg-white transition-colors"
                      title={isWishlisted(shed.id) ? 'Remove from saved' : 'Save'}
                    >
                      <FiHeart className={`w-4 h-4 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                    </button>
                  </div>

                  <Link to={`/shed/${shed.id}`} className="block">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'}
                        alt={shed.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3 flex gap-1.5">
                        {shed.salePrice && shed.salePrice < shed.price && (
                          <span className="bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">
                            Sale
                          </span>
                        )}
                        {shed.status === 'reserved' && (
                          <span className="bg-amber-500 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">
                            Reserved
                          </span>
                        )}
                      </div>
                      {shed.images?.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-0.5">
                          {shed.images.length} photos
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      {/* Status + Type */}
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">
                          {shed.shedType} &middot; {shed.size}
                        </p>
                        <span className={`flex items-center gap-1 text-[11px] font-medium ${shed.status === 'available' ? 'text-green-600' : shed.status === 'reserved' ? 'text-amber-600' : 'text-gray-400'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${shed.status === 'available' ? 'bg-green-500' : shed.status === 'reserved' ? 'bg-amber-500' : 'bg-gray-400'}`} />
                          {shed.status === 'available' ? 'Available' : shed.status === 'reserved' ? 'Reserved' : shed.status}
                        </span>
                      </div>

                      <h3 className="font-medium text-gray-900 mb-1 group-hover:underline">
                        {shed.name}
                      </h3>

                      {/* Dealer + Location */}
                      {shed.dealerName && (
                        <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" />
                          {shed.dealerName}{shed.dealerLocation ? ` — ${shed.dealerLocation}` : ''}
                        </p>
                      )}

                      {/* Price */}
                      <div className="flex items-baseline gap-2 mb-1">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="text-sm text-gray-400 line-through">
                              {formatPrice(shed.price)}
                            </span>
                            <span className="text-lg font-semibold text-red-600">
                              {formatPrice(shed.salePrice)}
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold text-gray-900">
                            {formatPrice(shed.price)}
                          </span>
                        )}
                        {shed.deliveryIncluded && (
                          <span className="text-xs text-gray-400 ml-auto">Free delivery</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 mb-3">
                        or from <span className="text-gray-600 font-medium">{getRtoMonthly(shed)}/mo</span> with Rent-to-Own
                      </p>

                      {/* Color Specs */}
                      <div className="flex gap-4 text-[11px] text-gray-500 border-t border-gray-100 pt-3 mt-1">
                        <div>
                          <span className="text-gray-400 block">Body</span>
                          <span className="text-gray-700">{shed.bodyColor || shed.sidingColor || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 block">Roof</span>
                          <span className="text-gray-700">{shed.roofColor || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 block">Trim</span>
                          <span className="text-gray-700">{shed.trimColor || '—'}</span>
                        </div>
                        {shed.brand && (
                          <div className="ml-auto text-right">
                            <span className="text-gray-400 block">Built by</span>
                            <span className="text-gray-700 font-medium">{shed.brand}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="space-y-px bg-gray-200 border border-gray-200">
              {filteredSheds.map((shed) => (
                <div key={shed.id} className="bg-white relative group flex flex-col md:flex-row">
                  {/* Wishlist Heart */}
                  <button
                    onClick={(e) => { e.preventDefault(); toggleWishlist(shed.id); }}
                    className="absolute top-3 left-3 md:left-auto md:right-3 z-10 p-1.5 bg-white/80 hover:bg-white transition-colors"
                  >
                    <FiHeart className={`w-4 h-4 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                  </button>

                  <Link to={`/shed/${shed.id}`} className="flex flex-col md:flex-row flex-1">
                    <div className="relative w-full md:w-64 h-48 md:h-auto flex-shrink-0">
                      <img
                        src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'}
                        alt={shed.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-10 md:left-3 flex gap-1.5">
                        {shed.salePrice && shed.salePrice < shed.price && (
                          <span className="bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">Sale</span>
                        )}
                        {shed.status === 'reserved' && (
                          <span className="bg-amber-500 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">Reserved</span>
                        )}
                      </div>
                      {shed.images?.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-0.5">
                          {shed.images.length} photos
                        </span>
                      )}
                    </div>
                    <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-400 uppercase tracking-wider">
                            {shed.shedType} &middot; {shed.size}
                          </p>
                          <span className={`flex items-center gap-1 text-[11px] font-medium ${shed.status === 'available' ? 'text-green-600' : shed.status === 'reserved' ? 'text-amber-600' : 'text-gray-400'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${shed.status === 'available' ? 'bg-green-500' : shed.status === 'reserved' ? 'bg-amber-500' : 'bg-gray-400'}`} />
                            {shed.status === 'available' ? 'Available' : shed.status === 'reserved' ? 'Reserved' : shed.status}
                          </span>
                        </div>
                        <h3 className="font-medium text-gray-900 mb-1 group-hover:underline">
                          {shed.name}
                        </h3>
                        {shed.dealerName && (
                          <p className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                            <FiMapPin className="w-3 h-3" />
                            {shed.dealerName}{shed.dealerLocation ? ` — ${shed.dealerLocation}` : ''}
                          </p>
                        )}
                        <div className="flex gap-4 text-[11px] text-gray-500 mt-2">
                          <span>Body: <span className="text-gray-700">{shed.bodyColor || shed.sidingColor || '—'}</span></span>
                          <span>Roof: <span className="text-gray-700">{shed.roofColor || '—'}</span></span>
                          <span>Trim: <span className="text-gray-700">{shed.trimColor || '—'}</span></span>
                          {shed.brand && <span className="ml-auto">Built by <span className="text-gray-700 font-medium">{shed.brand}</span></span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="block text-sm text-gray-400 line-through">
                              {formatPrice(shed.price)}
                            </span>
                            <span className="text-lg font-semibold text-red-600">
                              {formatPrice(shed.salePrice)}
                            </span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold text-gray-900">
                            {formatPrice(shed.price)}
                          </span>
                        )}
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {getRtoMonthly(shed)}/mo RTO
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShedInventory;
